import { useCallback, useState } from 'react'

import useAppState from '../../hooks/useAppState'
import { useUpdateUser } from '../../apiLayer/queryHooks/mutations'
import { sendErrorToSlack } from '../../libs/SlackHelper'
import { toError } from '../../../../pure-js/libs/Common'
import { DEFAULT_AI_COACH_VOICE_PRESET_ID } from '../../../../pure-js/libs/aiCoachVoicePresets'
import type { AiCoachPreferences } from '../../../../pure-js/types/Preferences'
import { DEFAULT_COACH_CONTACT_FREQUENCY, type CoachContactFrequency } from './coachPreferenceOptions'

export type UseCoachPreferencesResult = {
  contactsPerMonth: CoachContactFrequency
  voicePresetId: string
  isSaving: boolean
  /** Set when the last save failed, so the UI can say so instead of silently reverting. */
  saveFailed: boolean
  setContactFrequency: (contactsPerMonth: CoachContactFrequency) => void
  setVoicePreset: (voicePresetId: string) => void
}

/**
 * Reads and persists `preferences.aiCoach`. Saves are serialised through `isSaving` — the chips are
 * disabled while one is in flight, so a second choice cannot overwrite the first with stale state.
 */
export function useCoachPreferences(): UseCoachPreferencesResult {
  const { state, setState } = useAppState()
  const updateUserMutation = useUpdateUser()
  const [isSaving, setIsSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  const preferences = state.user?.preferences
  const contactsPerMonth = (preferences?.aiCoach?.contactsPerMonth ??
    DEFAULT_COACH_CONTACT_FREQUENCY) as CoachContactFrequency
  const voicePresetId = preferences?.aiCoach?.voicePresetId ?? DEFAULT_AI_COACH_VOICE_PRESET_ID

  const persist = useCallback(
    async (patch: Partial<AiCoachPreferences>) => {
      const userId = state.user?.id
      if (!userId || isSaving) return

      const aiCoach: AiCoachPreferences = { ...preferences?.aiCoach, ...patch, updatedAt: new Date().toISOString() }
      setIsSaving(true)
      setSaveFailed(false)
      try {
        await updateUserMutation.mutateAsync({ id: userId, userData: { preferences: { ...preferences, aiCoach } } })
        setState({ ...state, user: { ...state.user, preferences: { ...preferences, aiCoach } } })
      } catch (error) {
        // The stored value is unchanged, so surface it rather than showing a selection that did not save.
        setSaveFailed(true)
        void sendErrorToSlack(toError(error), undefined, 'useCoachPreferences.persist')
      } finally {
        setIsSaving(false)
      }
    },
    [isSaving, preferences, setState, state, updateUserMutation]
  )

  const setContactFrequency = useCallback(
    (nextFrequency: CoachContactFrequency) => void persist({ contactsPerMonth: nextFrequency }),
    [persist]
  )

  const setVoicePreset = useCallback(
    (nextVoicePresetId: string) => void persist({ voicePresetId: nextVoicePresetId }),
    [persist]
  )

  return { contactsPerMonth, voicePresetId, isSaving, saveFailed, setContactFrequency, setVoicePreset }
}
